import MultiAxisComponent from "@/components/AllocMultiAxisComponent3";
import React, { forwardRef, useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LogarithmicScale,
} from "chart.js";
import { GrPowerReset } from "react-icons/gr";
// Register the necessary components with Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LogarithmicScale
);

// Define a custom positioner
Tooltip.positioners.custom = function (elements, eventPosition) {
  if (!elements.length) {
    return false; // Return false if there are no elements
  }
  const chart = elements[0].element.$context.chart;
  return {
    x: chart.chartArea.left, // Adjust this value to add some padding from the left
    y: chart.chartArea.top, // Adjust this value to add some padding from the top
  };
};

const AllocCEAplot = forwardRef(
  ({ xAxis, yAxis, isMulti = false, coordinates = [] }, ref) => {
    const initialSlopePoints = [-2.0, -1.5, -1.0, -0.5, 0, 0.5, 1.0, 1.5, 2.0];
    const [slopePoints, setSlopePoints] = useState([]);
    const [abovePoint, setAbovePoint] = useState(0);
    // const [tooltipData, setTooltipData] = useState(null); // State variable for tooltip data
    const [onZoomClick, setOnZoomClick] = useState(false);
    const [isZoomedChart, setIsZoomedChart] = useState(false);
    const [vs, setVs] = useState("SPX");
    let pointLine = parseFloat(xAxis - yAxis).toFixed(1);
    useEffect(() => {
      setSlopePoints((prevValue) => computeSlope(xAxis, yAxis));
    }, [xAxis, yAxis]);

    useEffect(() => {
      if (isMulti) {
        setSlopePoints((prevValue) => findOverallMinMax(coordinates));
      }
    }, [coordinates]);

    const computeSlope = (x, y) => {
      let maxAbsValue = Math.max(2, Math.abs(x), Math.abs(y));
      let rangeRounding = Math.round(maxAbsValue * 2) / 2;

      let stepList = [];
      for (
        let i = -1 * rangeRounding - 1.0;
        i <= rangeRounding + 1.0;
        i += 0.5
      ) {
        stepList.push(i);
      }

      let filteredList = stepList.filter(
        (value) => Math.abs(value) <= rangeRounding + 1.5
      );

      return filteredList;
    };

    const findOverallMinMax = (data) => {
      let min = Infinity,
        max = -Infinity;

      data.forEach((item) => {
        let x = parseFloat(item.xAxis);
        let y = parseFloat(item.yAxis);

        min = Math.min(min, x, y);
        max = Math.max(max, x, y);
      });

      let maxAbsValue = Math.max(2, max);
      let rangeRounding = Math.round(maxAbsValue * 2) / 2;

      let stepList = [];
      for (
        let i = -1 * rangeRounding - 1.0;
        i <= rangeRounding + 1.0;
        i += 0.5
      ) {
        if (Math.abs(i) <= rangeRounding + 1.5) {
          stepList.push(i);
        }
      }
      return stepList;
    };

    const generateCordinatesDatasets = (cordinates) => {
      let mappedData = [];
      mappedData.push({
        label: `SPX Baseline`,
        data: [...slopePoints.map((point) => ({ x: point, y: point }))],
        borderColor: "rgba(105, 105, 105, 0.3)",
        backgroundColor: "rgba(105, 105, 105, 0.3)",
        pointRadius: 0,
        pointHitRadius: 0,
        showTooltip: false,
      });
      cordinates.map((point, idx) => {
        mappedData.push({
          label: `${point.asset} Cost Net`,
          data: [{ x: point.xAxis, y: parseFloat(point.xAxis - point.yAxis) }],
          borderColor: point.borderColor
            ? point.borderColor
            : "rgba(255, 0, 0, 0.3)",
          backgroundColor: point.borderColor
            ? point.borderColor
            : "rgba(255, 0, 0, 0.3)",
          pointRadius: 5,
        });
      });
      return mappedData;
    };
    const labels = [...slopePoints];

    const initialDatasets = [
      {
        label: `Cost Net`,
        data: [{ x: xAxis, y: parseFloat(xAxis - yAxis) }],
        borderColor: "rgba(255, 0, 0, 0.3)",
        backgroundColor: "rgba(255, 0, 0, 0.3)",
        pointRadius: 5,
      },
      {
        label: `SPX Baseline`,
        data: [...slopePoints.map((point) => ({ x: point, y: point }))], // Make sure points follow diagonal
        borderColor: "rgba(105, 105, 105, 0.3)",
        backgroundColor: "rgba(105, 105, 105, 0.3)",
        pointRadius: 0,
        pointHitRadius: 0,
      },
      {
        label: "",
        data: [
          { x: 0, y: 0 },
          { x: xAxis, y: 0 },
        ],
        borderColor: "rgba(254, 157, 82, 0.9)",
        pointRadius: 0,
        fill: false,
        type: "line",
        borderDash: [5, 3],
      },
      {
        label: "",
        data: [
          { x: xAxis, y: parseFloat(xAxis - yAxis) },
          { x: xAxis, y: parseFloat(xAxis + yAxis) },
        ],
        borderColor: "rgba(60, 151, 218, 0.8)",
        pointRadius: 0,
        fill: false,
        type: "line",
        borderDash: [2, 1],
      },
      {
        label: "",
        data: [
          { x: xAxis, y: 0 },
          { x: xAxis, y: parseFloat(xAxis - yAxis) },
        ],
        borderColor: "rgba(128, 0, 255, 0.9)",
        pointRadius: 0,
        fill: false,
        type: "line",
      },
    ];
    let datasets = isMulti
      ? [...generateCordinatesDatasets(coordinates)]
      : [...initialDatasets];

    function extractAfterPercentage(inputString) {
      const percentagePattern = /100%/;
      if (percentagePattern.test(inputString)) {
        const startIndex = inputString.indexOf("100%") + "100%".length;
        let result = inputString.substring(startIndex).trim();
        if (result.endsWith(")")) {
          result = result.slice(0, -1);
        }
        return result.trim();
      }
      return inputString;
    }

    const options = {
      animation: {
        duration: 0,
      },
      responsive: true,
      interaction: {
        mode: "point", // Show tooltip only on point
        intersect: true, // Ensure intersect is true to show tooltip only when directly over a point
      },
      hover: {
        mode: "point",
        intersect: true,
        animationDuration: 0, // Disable hover animation
      },
      plugins: {
        tooltip: {
          filter: function (tooltipItem) {
            return tooltipItem?.dataset?.label !== "";
          },
          position: "custom",
          caretSize: 0,
          callbacks: {
            label: function (tooltipItem) {
              let label = tooltipItem?.dataset?.label || "";
              if (isMulti) {
                if (coordinates && coordinates.length) {
                  for (let i = 0; i < coordinates.length; i++) {
                    if (tooltipItem?.datasetIndex === i + 1) {
                      let yAxisVal = parseFloat(coordinates[i]?.yAxis);
                      let xAxisVal = parseFloat(coordinates[i]?.xAxis);
                      let pointY = parseFloat(tooltipItem?.raw?.y || 0);
                      let diffFromBaseline = pointY - xAxisVal;

                      // If point is above baseline (positive diff), use positive yAxisVal
                      // If point is below baseline (negative diff), use negative yAxisVal
                      let displayYAxis =
                        diffFromBaseline > 0
                          ? Math.abs(yAxisVal)
                          : -Math.abs(yAxisVal);

                      label = `${tooltipItem?.dataset?.label}`;
                      label = label
                        .replace("Cost", `Cost (${xAxisVal})`)
                        .replace("Net", `Net (${displayYAxis})`);
                    }

                    if (typeof coordinates[i]?.asset === "undefined") {
                      // remove undefined string from label
                      label = label.replace("undefined", "").trim();
                    }
                  }
                }
              } else if (
                !isMulti &&
                coordinates.length === 0 &&
                !label.includes("SPX Baseline")
              ) {
                let yAxisVal = parseFloat(yAxis);
                let data = -yAxisVal;
                label += `: ${data}`;
              }
              const splitIndex = label.indexOf("Cost");
              if (splitIndex === -1) {
                return [label, ""];
              }
              const beforeCost = label.substring(0, splitIndex).trim();
              const afterCost = label.substring(splitIndex).trim();
              const firstElement = extractAfterPercentage(beforeCost);
              if (!firstElement) {
                return [afterCost];
              }
              return [firstElement, afterCost];
            },
            title: function () {
              return ""; // Return an empty string to hide the title
            },
          },
        },
        legend: {
          position: "top",
          labels: {
            filter: function (item, chart) {
              return;
            },
          },
        },
        title: {
          // display: true,
          // text: "98% SPX + 2.0% Insurance Versus SPX"
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
              speed: 0.1,
            },
            pinch: {
              enabled: true,
            },
            mode: "xy",
          },
          pan: {
            enabled: true,
            mode: "xy",
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          type: "linear",
          suggestedMin: -2.0,
          suggestedMax: 2.0,
          stepSize: 0.5,
          title: {
            display: true,
            text: "Arithmetric Cost (%)",
          },
          ticks: {
            callback: function (value, index, values) {
              return parseFloat(value).toFixed(1) + "%"; // limits to 2 decimal places
            },
          },
          grid: {
            color: (context) => {
              if (context.tick.label == "0.0%") {
                return "#878787";
              } else {
                return "#e1e1e1";
              }
            },
          },
        },
        y: {
          type: "linear",
          title: {
            display: true,
            text: "Geometric Cost (%)",
          },
          suggestedMin: -2.0,
          suggestedMax: 2.0,
          ticks: {
            callback: function (value, index, values) {
              return parseFloat(value).toFixed(1) + "%"; // limits to 2 decimal places
            },
          },
          position: "right",
          grid: {
            color: (context) => {
              if (context.tick.label == "0.0%") {
                return "#878787";
              } else {
                return "#e1e1e1";
              }
            },
          },
        },
      },
      elements: {
        point: {
          radius: 5, // Set the radius of the point
          hoverRadius: 10, // Increase the radius on hover to highlight the point
          backgroundColor: "rgba(75, 192, 192, 1)", // Highlight color for the point
          borderColor: "rgba(75, 192, 192, 1)", // Border color for the point
          borderWidth: 2, // Border width for the point
        },
      },
      events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"], // Add events to handle tooltip leave
    };
    return (
      <>
        <div className="relative">
          {isZoomedChart && (
            <div className="absolute top-0 -right-1">
              <button
                className="bg-[#69a4ce] rounded-full w-5 h-5 flex items-center justify-center text-white p-1"
                onClick={() => {
                  setOnZoomClick(true);
                  setTimeout(() => {
                    setOnZoomClick(false);
                  }, 0);
                }}
                data-testid="main-charts-zoom-reset-btn">
                <GrPowerReset />
              </button>
            </div>
          )}
          <MultiAxisComponent
            ref={ref}
            labels={labels}
            datasets={datasets?.filter(
              (res) => res?.label != "SPX Hedged (100% SPX) Cost Net"
            )}
            options={options}
            setIsZoomedChart={setIsZoomedChart}
            onZoomClick={onZoomClick}
            vs={vs}
          />
        </div>
      </>
    );
  }
);

export default AllocCEAplot;
